import { useDispatch, useSelector } from "react-redux";
import {
  getMapContentKeysWithPldKeys,
  getNodeById,
  patchPlds,
  refetchPldsForContent,
  relevantPldForContent,
  relevantPldsForContent,
  updateContentType,
} from "../state/graphLegacy";
import { AppDispatch, RootState } from "../../../common/state/store";
import { useEffect, useMemo, useState } from "react";
import {
  extractFieldsFromContentType,
  extractFieldsFromSchema,
  Field,
} from "../util/graphFieldsExtractor";
import { getContentFieldSchema } from "../state/contentFieldSchemaSlice";
import { Controller, useForm } from "react-hook-form";
import { IMedia } from "../../../model/unityObject";
import { ContentTypeScene } from "../util/dataTypes/ContentType";
import { PlusIcon, InformationCircleIcon } from "@heroicons/react/outline";
import AssistantScenarIaRail from "./AssistantScenarIaRail/AssistantScenarIaRail";
import { ScopeProvider } from "./AssistantScenarIaRail/ScopeContext";
import {
  getAllMedia,
  mediaSelector,
  setMediaSearch,
  setOriginalMediaGallery,
} from "../../gallery/state/gallerySlice";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { LoadingState, setIsLoading } from "../../../common/state/slice/appStateSlice";
import {
  closeConfirmationModal,
  openConfirmationModal,
  closeStandardModal,
  openStandardModal,
  isModalGalleryOpen,
  openMediaPreviewModal,
} from "../../../common/state/slice/modal/modalSlice";
import { DynamicField, DynamicMedia, prettyLabel } from "./DynamicField";
import ModalGallery from "./ModalGallery";
import { deleteNode, getCurrentProject } from "../../projects/state/projectsSlice";
import { useTranslation } from "react-i18next";
import { MdDeleteOutline } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "../../../common/components/Tooltip/Tooltip";
import { menuItem, menuItems } from "../../../common/components/Icon/ThreeDotsDropdownMenu";
import {
  getCurrentElements,
  getCurrentProjectGroupWeight,
  getProjectGroupSize,
  getSelectedProjectGroup,
} from "../../projectGroups/state/projectGroupsSlice";
import ModalConfirmation from "../../../common/components/Modal/ModalConfirmation";
import canSaveProject from "../util/canSaveProject";
import { FooterButtons } from "../../../common/components/FooterButtons";
import StartSceneTickbox from "./StartSceneTickbox";
import { useCallbackPrompt } from "../../../common/util/useCallbackPrompt";
import ModalConfirmBeforeNavigatingOut from "../../../common/components/Modal/ModalConfirmBeforeNavigatingOut";
import CustomXIcon from "src/common/components/CustomXIcon/CustomXIcon";
import { buildAllUpdatedPlds } from "../util/buildUpdatedProjectLanguageData";
import { applyPldTranslations } from "../util/applyPldTranslations";
import { MediaField } from "../util/graphFieldsExtractor";
import LanguageSwitcher from "./LanguageSwitcher";
import { computeStaleFields } from "../util/computeStaleFields";
import {
  DEFAULT_AVATAR_MEDIA_ID,
  findEmptyMediaRelationKeys,
  isEmptyMediaSentinel,
  isOptionalMediaField,
} from "../util/mediaRelationKeys";
import { translateProject } from "../../languages/state/languagesSlice";
import { usePrettyLanguageName } from "../../languages/util/prettyLanguageName";
import { setAlert, setHideAlert } from "src/features/alert/state/alertsSlice";
import Spinner from "src/common/components/Loader/Spinner";
import { currentOrgSelector } from "src/common/state/selectors/authSelector";
import RagAgentMultiSelect from "src/features/rag/components/RagAgentMultiSelect";
import QuizThreeAnswersField from "./fields/QuizThreeAnswersField";
import QuizImageMultipleAnswersField from "./fields/QuizImageMultipleAnswersField";
import QuizSortAnswersField from "./fields/QuizSortAnswersField";
import QuizRandomQuizzField from "./fields/QuizRandomQuizzField";

const NODE_COMPONENT_ID = "nodeComponentId";
const MAX_PROJECT_GROUP_MEDIA_SIZE_IN_BYTES = 16106127360;

const SLOPIFY_SUPPORTED_TYPES = new Set([
  "content-quizz-1",
  "content-quizz-2",
  "content-quizz-3",
  "content-quizz-sort",
  "content-quizz-image-multiple",
  "content-random-quizz",
  "content-yes-no-question",
  "content-text",
  "content-onboarding",
  "content-speech-to-check",
  "content-zone-detect",
  "content-element-catch",
]);

const isWideField = (field: Field): boolean => {
  if (field.type === "longtext" || field.type === "arrayOfObjects") return true;
  if (field.type === "text") {
    if (field.fieldName.endsWith("_text")) return true;
    const maxLen = field.descriptor?.jsonSchema?.maxLength;
    if (typeof maxLen === "number" && maxLen > 80) return true;
  }
  return false;
};

// Mirrors apps/backend-strapi/src/utils/constants/pldFieldsThatIncludeTranslatableStuff.js
// — kept inline (and small) so the overwrite check doesn't drag the whole
// constants module into the frontend bundle.
const PLD_TRANSLATABLE_TEXT_KEYS = [
  "text",
  "title",
  "gazed_text",
  "text_success",
  "text_fail",
  "question",
  "answer1",
  "answer2",
  "answer3",
  "explanation_true",
  "explanation_false",
  "intro_text",
  "end_text",
];
const PLD_TRANSLATABLE_JSON_KEYS = [
  "list_answers",
  "list_hotspot_data",
  "list_keyword_data",
  "list_hotspot_zone_sort_data",
  "list_answer_quizz_sort_data",
  "list_question_random_quizz_data",
  "list_element_catch_element_data",
];

const pldHasTranslatableContent = (pld: any): boolean => {
  if (!pld) return false;
  for (const k of PLD_TRANSLATABLE_TEXT_KEYS) {
    const v = pld[k];
    if (typeof v === "string" && v.trim().length > 0) return true;
  }
  for (const k of PLD_TRANSLATABLE_JSON_KEYS) {
    const v = pld[k];
    if (Array.isArray(v) && v.length > 0) return true;
  }
  return false;
};

const Node = ({
  nodeId,
  contentType,
  mode,
}: {
  nodeId: string;
  contentType: string;
  mode: "edit" | "view";
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const prettyLanguageName = usePrettyLanguageName();
  const navigate = useNavigate();
  const location = useLocation();

  const mediasList = useSelector(mediaSelector);
  const currentProject = useSelector(getCurrentProject);
  const currentGroupProject = useSelector(getSelectedProjectGroup);
  const currentElements = useSelector(getCurrentElements);
  const mapContentKeysWithPldKeys = useSelector(getMapContentKeysWithPldKeys);
  const node = useSelector((state: RootState) => getNodeById(state, nodeId, contentType));
  const projectLanguageDatasOfThisContent = useSelector(
    relevantPldsForContent(node?.object.content),
  );
  const currentProjectGroupWeight = useSelector(getCurrentProjectGroupWeight);
  const org = useSelector(currentOrgSelector);
  const contentFieldSchema = useSelector((state: RootState) =>
    node ? getContentFieldSchema(node.modelName)(state) : null,
  );

  const [stopPlayingMedia, setStopPlayingMedia] = useState(false);
  const [fieldsToDisplay, setFieldsToDisplay] = useState<Field[]>([]);
  const [enhancedFieldsToDisplay, setEnhancedFieldsToDisplay] = useState<IEnhancedFieldToDisplay[]>(
    [],
  );
  const [currentMediaField, setCurrentMediaField] = useState<string>("");
  // When picking a per-answer image for content-quizz-image-multiple, this
  // holds the target answer row index (null otherwise). The shared gallery
  // modal's onMediaChange routes the selection into list_answers[idx].imageId.
  const [quizzImagePickerIndex, setQuizzImagePickerIndex] = useState<number | null>(null);
  // Same idea for content-random-quizz, but its answers are doubly-nested, so
  // the picker target is { q: questionIndex, a: answerIndex }. onMediaChange
  // routes the selection into questions[q].answers[a].imageId.
  const [quizzRandomImagePicker, setQuizzRandomImagePicker] = useState<{
    q: number;
    a: number;
  } | null>(null);
  const [areButtonsDisabled, setAreButtonDisabled] = useState(false);
  const [isStartScene, setIsStartScene] = useState(node && node.object.is_start_scene);
  const [hasMediaChanged, setHasMediaChanged] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isComputingPgSize, setIsComputingPgSize] = useState(false);
  const [isOverProjectGroupMediaWeightLimit, setIsOverProjectGroupMediaWeightLimit] = useState(
    currentProjectGroupWeight > MAX_PROJECT_GROUP_MEDIA_SIZE_IN_BYTES,
  );
  const [allowedMediaTypeOverride, setAllowedMediaTypeOverride] = useState<IMedia["type"] | null>(
    null,
  );
  const [isGenerating, setIsGenerating] = useState(false);
  useEffect(() => {
    if (!isGenerating) return undefined;
    dispatch(
      setAlert({
        id: "slopify-generation",
        type: "success",
        msg: t("general.assistantScenarIa.formDisabledBanner"),
        customTimeoutInMs: 30000,
      }),
    );
    return () => {
      dispatch(setHideAlert({ id: "slopify-generation" }));
    };
  }, [isGenerating, dispatch, t]);
  const [hasGeneratedContent, setHasGeneratedContent] = useState(false);
  const [justApplied, setJustApplied] = useState<Set<string>>(() => new Set());
  const [isTranslating, setIsTranslating] = useState(false);
  // Holds the target language IDs the user is about to translate to while
  // the overwrite confirmation modal is open. null when no confirm is
  // pending. Cleared on submit/cancel.
  const [pendingTranslateTargetIds, setPendingTranslateTargetIds] = useState<number[] | null>(null);

  const sourceLanguageId: number = currentProject?.language?.id
    ? currentProject.language.id
    : currentProject?.enabled_languages?.length > 0
    ? currentProject.enabled_languages[0].id
    : Number.isFinite(Number(currentProject?.language))
    ? Number(currentProject?.language)
    : 1;
  const [activeLanguageId, setActiveLanguageId] = useState<number>(sourceLanguageId);
  useEffect(() => {
    if (sourceLanguageId && activeLanguageId !== sourceLanguageId) {
      setActiveLanguageId(sourceLanguageId);
    }
  }, [sourceLanguageId]);

  const modalGalleryIsOpen = useSelector(isModalGalleryOpen);

  interface IEnhancedFieldToDisplay {
    fields: MediaField;
    relevantSourceMedia?: IMedia;
  }

  const spinnerComponent = useMemo(() => {
    if (modalGalleryIsOpen) {
      return (
        <div key={`${mode}-spinner`} className="mt-40 w-80 mr-9">
          <Spinner relative />
        </div>
      );
    }
    return null;
  }, [modalGalleryIsOpen, mode]);

  const {
    register,
    reset,
    setValue,
    handleSubmit,
    getValues,
    control,
    watch,
    formState: { dirtyFields },
  } = useForm<any>();

  const nameCantBeEmpty = watch("name") ? watch("name").length > 0 : false;

  // Live array lengths drive the rail's per-item ("Answer N") scope units.
  // Lengths (not contents) are what change when rows are added/removed.
  const listAnswersCount = (watch("list_answers") as unknown[] | undefined)?.length;
  const answersCount = (watch("answers") as unknown[] | undefined)?.length;
  const questionsCount = (watch("questions") as unknown[] | undefined)?.length;

  const contentId = node?.object.content;
  const relevantPld = useSelector(relevantPldForContent(contentId))?.projectLanguageData;
  const activePld = useMemo(() => {
    const match = projectLanguageDatasOfThisContent?.find(
      (p: any) => Number(p.language) === Number(activeLanguageId),
    );
    return match ?? relevantPld;
  }, [projectLanguageDatasOfThisContent, activeLanguageId, relevantPld]);
  const staleFieldsByLanguageId = useMemo(() => {
    // Runs during render: a throw here would be caught by the app ErrorBoundary
    // and bounce the user to /error, losing in-progress edits. Stale highlights
    // are cosmetic, so degrade to "none" rather than crash the page.
    try {
      return computeStaleFields({
        plds: projectLanguageDatasOfThisContent as any,
        sourceLanguageId,
        schema: contentFieldSchema,
      });
    } catch (e) {
      console.error("computeStaleFields failed; rendering without stale highlights", e);
      return {} as Record<number, Set<string>>;
    }
  }, [projectLanguageDatasOfThisContent, sourceLanguageId, contentFieldSchema]);
  const activeStaleFields = staleFieldsByLanguageId[activeLanguageId];
  const isEditingSourceLanguage = Number(activeLanguageId) === Number(sourceLanguageId);

  const onSubmit = () => {
    // Maybe this has some use, but atm (1/08/22) only thing it does is cause a bug (make API call on image click)
    //onUpdateNode();
  };

  useEffect(() => {
    dispatch(closeStandardModal("modal-gallery"));
    return setHasMediaChanged(false);
  }, []);

  useEffect(() => {
    if (!modalGalleryIsOpen) {
      setAllowedMediaTypeOverride(null);
      // Clear any pending per-answer image target so a subsequent scalar-media
      // pick (e.g. a win/loose sound) isn't misrouted into list_answers.
      setQuizzImagePickerIndex(null);
    }
  }, [modalGalleryIsOpen]);

  useEffect(() => {
    const updatedEnhancedFields: IEnhancedFieldToDisplay[] = fieldsToDisplay
      .map((field: MediaField) => {
        const relevantSourceMedia = mediasList.find(
          (m: IMedia) => Number(m.id) === Number(field.currentValue),
        );
        return {
          fields: field,
          relevantSourceMedia,
        };
      })
      .filter((field) => field.fields.type === "media");

    setEnhancedFieldsToDisplay(updatedEnhancedFields);
  }, [fieldsToDisplay, mediasList, relevantPld]);

  useEffect(() => {
    dispatch(setIsLoading({ componentId: NODE_COMPONENT_ID, loading: LoadingState.Idle }));
    if (node) {
      const fields = contentFieldSchema
        ? extractFieldsFromSchema(node, contentFieldSchema, activePld)
        : extractFieldsFromContentType(node);
      setFieldsWithPldValues({ payload: fields });
      // Push the freshly-extracted PLD values into react-hook-form. Without
      // this reset, the uncontrolled <textarea defaultValue={…}> ignores the
      // new prop because RHF retains the prior tracked value across renders
      // (default `shouldUnregister: false`). This is what made retranslation
      // appear to do nothing — the DB had the new text, the form didn't.
      const nextFormValues: Record<string, unknown> = {};
      for (const f of fields) {
        if (f.type === "media" || f.type === "boolean") continue;
        nextFormValues[f.fieldName] = f.currentValue ?? "";
      }
      reset(nextFormValues, { keepDirty: false, keepDefaultValues: false });
      if (
        node.modelName === "content-avatar-ai-persona" ||
        node.modelName === "content-avatar-ai"
      ) {
        setValue("rag_agent_ids", (node.object as Record<string, unknown>).rag_agent_ids || []);
        if (node.modelName === "content-avatar-ai-persona") {
          setValue(
            "rag_agent_ids_debrief",
            (node.object as Record<string, unknown>).rag_agent_ids_debrief || [],
          );
        }
      }
      // has_multiple_answers lives on the base content (not in the slopify
      // schema, so not among the extracted fields). Seed it so the bespoke
      // quizz-3 editor can read/write it and the save path round-trips it.
      if (node.modelName === "content-quizz-3") {
        setValue(
          "has_multiple_answers",
          !!(node.object as Record<string, unknown>).has_multiple_answers,
        );
      }
      // content-quizz-image-multiple's list_answers is polymorphic: the content
      // object holds the full [{ text, imageId, is_right }] objects (structural,
      // shared across languages) while the PLD holds only a .text string[]. The
      // schema extractor is PLD-first, so it would hand the editor a bare
      // string[] and drop imageId/is_right. Re-seed RHF with the full objects,
      // overlaying the active language's PLD text on top of the content
      // structure so translations show while images/correctness round-trip.
      if (node.modelName === "content-quizz-image-multiple") {
        const contentAnswers: any[] = Array.isArray(
          (node.object as Record<string, unknown>).list_answers,
        )
          ? ((node.object as Record<string, unknown>).list_answers as any[])
          : [];
        const pldTexts: any[] = Array.isArray((activePld as any)?.list_answers)
          ? (activePld as any).list_answers
          : [];
        const seeded = contentAnswers.map((a, i) => ({
          text: typeof pldTexts[i] === "string" ? pldTexts[i] : a?.text ?? "",
          imageId: typeof a?.imageId === "number" ? a.imageId : a?.imageId ?? null,
          is_right: !!a?.is_right,
        }));
        setValue("list_answers", seeded);
      }
      // content-quizz-sort's answers is polymorphic the same way: the content
      // object holds the full [{ text, index, id_internal }] objects (index =
      // correct position, id_internal = stable identity, both shared across
      // languages) while the PLD holds only [{ text, id_internal }] (no index).
      // The schema extractor is PLD-first, so it would hand the editor the
      // index-less PLD shape. Re-seed RHF with the full content objects,
      // overlaying the active language's PLD text matched BY id_internal (the
      // PLD may be reordered or empty in a target language) so translations
      // show while index/id_internal round-trip.
      if (node.modelName === "content-quizz-sort") {
        const contentAnswers: any[] = Array.isArray(
          (node.object as Record<string, unknown>).answers,
        )
          ? ((node.object as Record<string, unknown>).answers as any[])
          : [];
        const pldAnswers: any[] = Array.isArray((activePld as any)?.list_answer_quizz_sort_data)
          ? (activePld as any).list_answer_quizz_sort_data
          : [];
        const seeded = contentAnswers.map((a) => {
          const pldMatch = pldAnswers.find(
            (p) => Number(p?.id_internal) === Number(a?.id_internal),
          );
          return {
            text: typeof pldMatch?.text === "string" ? pldMatch.text : a?.text ?? "",
            index: typeof a?.index === "number" ? a.index : 0,
            id_internal: typeof a?.id_internal === "number" ? a.id_internal : 0,
          };
        });
        setValue("answers", seeded);
      }
      // content-random-quizz's questions is doubly-nested and polymorphic too,
      // but UNLIKE sort/image-multiple the PLD stores the FULL object wrapped
      // under `question` (not a text-only projection). The content object is
      // still the structural source of truth; the active-language PLD carries
      // the translated text. Re-seed RHF from the content structure, overlaying
      // the PLD's translated text BY POSITION (questions have no per-element id;
      // order is structural/source-stable). The target PLD is frequently empty
      // (untranslated) — fall back to the content's source text then.
      if (node.modelName === "content-random-quizz") {
        const contentQuestions: any[] = Array.isArray(
          (node.object as Record<string, unknown>).questions,
        )
          ? ((node.object as Record<string, unknown>).questions as any[])
          : [];
        const pldQuestions: any[] = Array.isArray(
          (activePld as any)?.list_question_random_quizz_data,
        )
          ? (activePld as any).list_question_random_quizz_data
          : [];
        const seeded = contentQuestions.map((q, qi) => {
          // PLD elements are wrapped: { question: {...} }.
          const pldQ = pldQuestions[qi]?.question ?? pldQuestions[qi];
          const contentAnswers: any[] = Array.isArray(q?.answers) ? q.answers : [];
          const pldAnswers: any[] = Array.isArray(pldQ?.answers) ? pldQ.answers : [];
          return {
            text: typeof pldQ?.text === "string" ? pldQ.text : q?.text ?? "",
            winText: typeof pldQ?.winText === "string" ? pldQ.winText : q?.winText ?? "",
            loseText:
              typeof pldQ?.loseText === "string" ? pldQ.loseText : q?.loseText ?? "",
            // Structural fields always come from the content object.
            imageAnswers: !!q?.imageAnswers,
            allowMultiple: !!q?.allowMultiple,
            answers: contentAnswers.map((a, ai) => ({
              text:
                typeof pldAnswers[ai]?.text === "string"
                  ? pldAnswers[ai].text
                  : a?.text ?? "",
              imageId:
                a?.imageId === null || a?.imageId === undefined ? null : Number(a.imageId),
              is_right: !!a?.is_right,
            })),
          };
        });
        setValue("questions", seeded);
      }
    }
  }, [node?.object.id, contentFieldSchema, activeLanguageId, activePld]);

  // on mount we (pre/re-)fetch all the medias.
  // NB: NO componentId on purpose. getAllMedia is the UNPAGINATED full-library
  // fetch (used only for filter-dropdown config + state.all); the gallery grid
  // is paginated and driven by fetchFilteredMedias. Sharing MEDIA_LIST_COMPONENT_ID
  // made the fast paginated grid wait behind this slow fetch's loading state.
  // Fire-and-forget like App.tsx so it never gates the grid.
  useEffect(() => {
    if (org) {
      dispatch(getAllMedia({ orgId: Number(org.id) }));
    }
  }, [dispatch, org]);

  const hasSomethingChanged = () => {
    if (isSaved) return false;
    const objectKeys = Object.keys(dirtyFields);
    const butWithoutMediaFields = objectKeys.filter((k) => !k.includes("source_"));
    const hasSmthChanged = Boolean(
      butWithoutMediaFields?.length > 0 ||
        hasMediaChanged ||
        hasGeneratedContent ||
        (node && node.object.is_start_scene !== isStartScene),
    );
    return hasSmthChanged;
  };

  const onUpdateNode = () => {
    if (node) {
      setValue("is_start_scene", isStartScene);

      const changes = getValues();

      // Personas can reach the editor without a source_avatar relation
      // (ScenarIA rows predating the 2026.07.24 backfill, or any future
      // creation path that skips it). The Unity clients self-heal these with
      // the shared default avatar on save — do the same here instead of
      // dead-ending the save on the required-media guard below.
      if (
        node.modelName === "content-avatar-ai-persona" &&
        isEmptyMediaSentinel(changes.source_avatar)
      ) {
        changes.source_avatar = DEFAULT_AVATAR_MEDIA_ID;
      }

      if (findEmptyMediaRelationKeys(changes, String(node.modelName)).length > 0) {
        dispatch(setAlert({ type: "danger", msg: t("alerts.contentUpdateMissingMedia") }));
        return;
      }

      setHasMediaChanged(false);

      setAreButtonDisabled(true);
      dispatch(setIsLoading({ componentId: NODE_COMPONENT_ID, loading: LoadingState.Loading }));
      setIsStartScene(node && node.object.is_start_scene);
      setIsSaved(true);

      // Added 11/06/24: handle PLD update there.
      // Defensive: buildAllUpdatedPlds derefs activePld.id; a missing/late PLD
      // would throw straight out of this click handler (uncatchable by the app
      // ErrorBoundary) and wedge the form after the content itself may already
      // have been written. Abort cleanly instead of crashing or half-saving.
      let updatedProjectLanguageDatas;
      try {
        updatedProjectLanguageDatas = buildAllUpdatedPlds({
          node,
          changes,
          activePld,
          mapContentKeysWithPldKeys,
        });
      } catch (e) {
        console.error("buildAllUpdatedPlds failed; aborting save to avoid corrupt PLD write", e);
        dispatch(setAlert({ type: "danger", msg: t("alerts.contentUpdateFail") }));
        dispatch(setIsLoading({ componentId: NODE_COMPONENT_ID, loading: LoadingState.Idle }));
        setAreButtonDisabled(false);
        setIsSaved(false);
        return;
      }

      dispatch(
        updateContentType({
          componentId: NODE_COMPONENT_ID,
          contentType: node as ContentTypeScene,
          updatedProjectLanguageDatas,
          changes,
          activeLanguageId,
          sourceLanguageId,
          callback: () => {
            dispatch(
              setIsLoading({ componentId: NODE_COMPONENT_ID, loading: LoadingState.Success }),
            );
            dispatch(setAlert({ type: "success", msg: t("alerts.contentUpdateSuccess") }));

            if (location.pathname.includes("templates")) {
              navigate(`/templates/${currentProject.id}`);
            } else {
              navigate("../graph");
            }
          },
          allMedias: mediasList,
        }),
      ).catch(() => {
        dispatch(setAlert({ type: "danger", msg: t("alerts.contentUpdateFail") }));
      });
    }
  };

  const isSlopifySupported = node ? SLOPIFY_SUPPORTED_TYPES.has(node.modelName) : false;

  const dirtyFieldNames = useMemo(() => Object.keys(dirtyFields), [dirtyFields]);

  const otherEnabledLanguageIds = useMemo<number[]>(() => {
    const enabled = currentProject?.enabled_languages ?? [];
    return enabled
      .map((l: any) => Number(l?.id))
      .filter((id: number) => Number.isFinite(id) && id !== Number(sourceLanguageId));
  }, [currentProject, sourceLanguageId]);

  const translatableFieldNames = useMemo<string[]>(() => {
    return (contentFieldSchema?.fields ?? []).filter((f) => f.isTranslatable).map((f) => f.key);
  }, [contentFieldSchema]);

  const handleApplyLanguageTranslations = async (
    languageId: number,
    translations: Record<string, unknown>,
  ) => {
    if (!node) return;
    try {
      const { pldId, mappedPayload } = await applyPldTranslations({
        languageId,
        translations,
        contentType: String(node.modelName),
        plds: projectLanguageDatasOfThisContent as any,
        mapContentKeysWithPldKeys,
      });
      dispatch(patchPlds([{ id: pldId, changes: mappedPayload as any }]));
      dispatch(
        setAlert({
          type: "success",
          msg: t("pages.project.editNode.propagationLanguageApplied"),
        }),
      );
    } catch (err) {
      dispatch(
        setAlert({
          type: "danger",
          msg: t("general.error"),
        }),
      );
    }
  };

  const handleFieldsApplied = (fieldNames: string[]) => {
    if (fieldNames.length === 0) return;
    setJustApplied((prev) => {
      const next = new Set(prev);
      fieldNames.forEach((f) => next.add(f));
      return next;
    });
    window.setTimeout(() => {
      setJustApplied((prev) => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        fieldNames.forEach((f) => next.delete(f));
        return next;
      });
    }, 400);
  };

  // HANDLE PROJECT LANGUAGE DATA MESS
  const setFieldsWithPldValues = ({
    payload,
    isHappeningAfterMediaUpdate = false,
  }: {
    payload: Field[];
    isHappeningAfterMediaUpdate?: boolean;
  }) => {
    // Defensive: this reconciles content media against per-language PLD media
    // and runs both from the media-change handler and from a mount effect. A
    // malformed PLD/key map shouldn't blank the form or crash render — on
    // failure fall back to displaying the raw (source-language) fields.
    try {
      let updatedPld = {
        ...relevantPld,
      };
      //  dispatch(updateRelevantPld({ contentId: relevantPld.content, updatedPld }));

      const payloadUpdatedWithPldValues = payload.map((field: Field) => {
        if (field.type !== "media") {
          return field;
        }
        const mediaField = field as MediaField;
        const pldKey = getRelevantPldKey({
          contentKey: mediaField.fieldName,
          mapContentKeysWithPldKeys: mapContentKeysWithPldKeys,
          contentType,
        });
        const relevantMediaIdInPldBeforeUpdate = pldKey ? relevantPld?.[pldKey] : null;
        if (pldKey && relevantMediaIdInPldBeforeUpdate !== mediaField.currentValue) {
          updatedPld = {
            ...updatedPld,
            [pldKey]: mediaField.currentValue,
          };
        }
        return {
          ...mediaField,
          currentValue:
            relevantMediaIdInPldBeforeUpdate && !isHappeningAfterMediaUpdate
              ? relevantMediaIdInPldBeforeUpdate
              : mediaField.currentValue,
        };
      });
      setFieldsToDisplay(payloadUpdatedWithPldValues);
      payloadUpdatedWithPldValues.forEach((field) => {
        if (field.type === "arrayOfObjects") {
          // content-quizz-image-multiple's list_answers is seeded as a full
          // object array by the mount effect (the PLD-derived currentValue here
          // is only the .text string[]). Pushing currentValue would clobber the
          // objects — and any unsaved answer edits — every time this runs (it
          // also fires after a media change, e.g. swapping a win/loose sound).
          // Leave it to the mount-effect seeding + the bespoke editor.
          if (
            node?.modelName === "content-quizz-image-multiple" &&
            field.fieldName === "list_answers"
          ) {
            return;
          }
          // content-quizz-sort's answers is likewise seeded as a full object
          // array (the PLD-derived value here drops `index`). Same reasoning:
          // leave it to the mount-effect seeding + the bespoke editor.
          if (node?.modelName === "content-quizz-sort" && field.fieldName === "answers") {
            return;
          }
          // content-random-quizz's questions is likewise seeded as a full
          // object array by the mount effect (the PLD-derived value here is the
          // wrapped/text shape). Leave it to the seeding + bespoke editor.
          if (node?.modelName === "content-random-quizz" && field.fieldName === "questions") {
            return;
          }
          setValue(field.fieldName, field.currentValue);
        }
      });
    } catch (e) {
      console.error("setFieldsWithPldValues failed; falling back to raw fields", e);
      setFieldsToDisplay(payload);
    }
  };

  const getRelevantPldKey = ({
    contentKey,
    mapContentKeysWithPldKeys,
    contentType,
  }: {
    contentKey: string;
    mapContentKeysWithPldKeys: Record<string, [string, string][]>;
    contentType: string;
  }): string | null => {
    const mapEntries = Object.entries(mapContentKeysWithPldKeys);
    const match = mapEntries.find((entry) => entry[0].includes(contentType));

    if (match) {
      const keyMap = match[1].reduce((acc, [index0, index1]) => {
        acc[index1] = index0;
        return acc;
      }, {});
      return keyMap[contentKey];
    } else {
      console.warn(
        "NO match in getRelevantPldKey; contentKey: ",
        contentKey,
        ".. contentType: ",
        contentType,
      );
    }
    return null;
  };

  // Opens the shared gallery modal for a content-quizz-image-multiple answer
  // row. The selection is routed by onMediaChange's early branch below.
  const onPickAnswerImage = (index: number) => {
    setAllowedMediaTypeOverride("image");
    setQuizzImagePickerIndex(index);
    dispatch(setMediaSearch({ mediaType: "image" }));
    const current = getValues("list_answers");
    const currentImageId = Array.isArray(current) ? current[index]?.imageId : undefined;
    dispatch(setOriginalMediaGallery(currentImageId));
    dispatch(openStandardModal("modal-gallery"));
  };

  // Opens the shared gallery modal for a content-random-quizz answer at
  // (qIndex, aIndex). Routed by onMediaChange's early branch below.
  const onPickRandomAnswerImage = (qIndex: number, aIndex: number) => {
    setAllowedMediaTypeOverride("image");
    setQuizzRandomImagePicker({ q: qIndex, a: aIndex });
    dispatch(setMediaSearch({ mediaType: "image" }));
    const current = getValues("questions");
    const currentImageId = Array.isArray(current)
      ? current[qIndex]?.answers?.[aIndex]?.imageId
      : undefined;
    dispatch(setOriginalMediaGallery(currentImageId));
    dispatch(openStandardModal("modal-gallery"));
  };

  const onMediaChange = (selectedMedia: IMedia | null, mediaField?: string | null) => {
    // content-random-quizz per-answer image: route the picked media into
    // questions[q].answers[a].imageId. Same early-return rationale as
    // image-multiple below (skip scalar/scene bookkeeping + the trailing
    // setFieldsWithPldValues that would clobber questions).
    if (quizzRandomImagePicker !== null) {
      const { q, a } = quizzRandomImagePicker;
      const current = getValues("questions");
      const arr = Array.isArray(current) ? [...current] : [];
      if (arr[q]?.answers?.[a]) {
        const answers = [...arr[q].answers];
        answers[a] = {
          ...answers[a],
          imageId: selectedMedia?.id ? Number(selectedMedia.id) : null,
        };
        arr[q] = { ...arr[q], answers };
        setValue("questions", arr, { shouldDirty: true });
      }
      setQuizzRandomImagePicker(null);
      dispatch(closeStandardModal("modal-gallery"));
      return;
    }
    // content-quizz-image-multiple per-answer image: route the picked media
    // into list_answers[idx].imageId instead of a scalar form field. Early
    // return so we skip the scene/scalar media bookkeeping (and the trailing
    // setFieldsWithPldValues, which would clobber list_answers with the
    // PLD-derived value).
    if (quizzImagePickerIndex !== null) {
      const idx = quizzImagePickerIndex;
      const current = getValues("list_answers");
      const arr = Array.isArray(current) ? [...current] : [];
      if (arr[idx]) {
        arr[idx] = {
          ...arr[idx],
          imageId: selectedMedia?.id ? Number(selectedMedia.id) : null,
        };
        setValue("list_answers", arr, { shouldDirty: true });
      }
      setQuizzImagePickerIndex(null);
      dispatch(closeStandardModal("modal-gallery"));
      return;
    }

    // Until next comment, it is trash code to compute & compare medias size in bytes before & after
    const isolateMediaFieldsMedia = (fieldsToDisplay: any[]) => {
      return [...mediasList].filter((m) => {
        const mediaFields = fieldsToDisplay
          .filter((ftd) => ftd.fieldName.includes("source_"))
          .map((ftd) => Number(ftd.currentValue));
        return mediaFields.includes(Number(m.id));
      });
    };

    const sumOfSizeInBytes = (mediaFieldsArr: any[]) =>
      mediaFieldsArr.map((m) => Number(m.size_in_bytes)).reduce((a, b) => a + b, 0);

    const mediaFieldsMediasBefore = isolateMediaFieldsMedia(fieldsToDisplay);

    const sumOfSizeInBytesBeforeChangeForThisNode = mediaFieldsMediasBefore
      .map((m) => Number(m.size_in_bytes))
      .reduce((a, b) => a + b, 0);

    const fieldsToDisplayAfterChange = fieldsToDisplay.map((ftd) => {
      if (ftd.fieldName === currentMediaField) {
        return { ...ftd, currentValue: Number(selectedMedia?.id) };
      }
      return ftd;
    });

    const mediaFieldsMediasAfter = isolateMediaFieldsMedia(fieldsToDisplayAfterChange);
    const sumOfSizeInBytesAfterChangeForThisNode = sumOfSizeInBytes(mediaFieldsMediasAfter);

    // project can be null at runtime (orphaned PG link row); guard before deref
    const projectIdsForSize = (currentElements ?? [])
      .map((pge) => pge.project?.id)
      .filter((id): id is number => id != null)
      .join(",");

    if (projectIdsForSize.length) {
      setIsComputingPgSize(true);
      dispatch(
        getProjectGroupSize({
          projectsIds: projectIdsForSize,
        }),
      )
        .then((res: any) => {
          // Defensive: getProjectGroupSize is loosely-typed and can resolve to
          // an error-shaped payload (no .Data). Dereferencing blindly threw an
          // uncaught rejection on media change. Only refine the flag when we got
          // a real number; .finally below sets a safe fallback regardless.
          const projectGroupSize = Number(res?.payload?.Data);
          if (Number.isFinite(projectGroupSize)) {
            setIsOverProjectGroupMediaWeightLimit(
              projectGroupSize -
                sumOfSizeInBytesBeforeChangeForThisNode +
                sumOfSizeInBytesAfterChangeForThisNode >
                MAX_PROJECT_GROUP_MEDIA_SIZE_IN_BYTES,
            );
          }
        })
        .catch((e: unknown) => {
          // Best-effort size check; the .finally fallback below uses the
          // locally-known weight, so we only lose the precise PG total here.
          console.error("getProjectGroupSize failed during media change", e);
        })
        .finally(() => {
          setIsComputingPgSize(false);

          setIsOverProjectGroupMediaWeightLimit(
            currentProjectGroupWeight -
              sumOfSizeInBytesBeforeChangeForThisNode +
              sumOfSizeInBytesAfterChangeForThisNode >
              MAX_PROJECT_GROUP_MEDIA_SIZE_IN_BYTES,
          );
        });
    }

    // Old stuff
    const mf = mediaField && mediaField !== "" ? mediaField : currentMediaField;
    const selectedMediaId = selectedMedia?.id ? selectedMedia?.id : null;

    setValue(mf, selectedMediaId);

    const updatedFields: any = fieldsToDisplay.map((field) => {
      if (field.fieldName === mf) {
        if (Number(field.currentValue) !== Number(selectedMedia?.id)) {
          setHasMediaChanged(true);
        }
        return { ...field, currentValue: selectedMedia?.id };
      }
      return field;
    });

    setFieldsWithPldValues({ payload: updatedFields, isHappeningAfterMediaUpdate: true });
    dispatch(closeStandardModal("modal-gallery"));
  };

  const onRemove = () => {
    setAreButtonDisabled(true);
    dispatch(closeConfirmationModal("remove-node-confirmation"));
    dispatch(setIsLoading({ componentId: NODE_COMPONENT_ID, loading: LoadingState.Loading }));
    dispatch(deleteNode({ contentId: node?.object.content })).then(() => {
      if (location.pathname.includes("templates")) {
        navigate(`/templates/${currentProject.id}`);
      } else {
        navigate(`/courses/${currentGroupProject.id}/list/${currentProject.id}/graph`);
      }
    });
  };

  const getCanDelete = () => {
    if (node?.object.is_start_scene) {
      return { canDelete: false, reason: "pages.project.editNode.canNotDeleteStartScene" };
    }
    return { canDelete: true, reason: "" };
  };
  const canDelete = getCanDelete();

  const canSave = canSaveProject(currentProject);

  const model3dMenuItems: menuItems = [
    { menuItemText: t("general.delete"), icon: "DocumentRemoveIcon", itemId: "deleteEnv3d" },
  ];

  const handleOpenGallery = (enhancedField: any, menuItem: menuItem | null) => {
    const mediaType = enhancedField.fields.type === "media" ? enhancedField.fields.mediaType : null;

    // Check if this is the source_model3d field (3D Environment)
    const isModel3dField = enhancedField.fields.fieldName === "source_model3d";

    // The field's ACCEPTED media types (fixed). Drives the gallery's available
    // options AND the picker's validation gate (ModalGallery). A scene accepts
    // image OR video, hence "image|video".
    const acceptedMediaTypes = isModel3dField
      ? "model3d" // Lock to model3d for 3D environment
      : contentType === "content-scene"
      ? "image|video"
      : mediaType ?? null;
    setAllowedMediaTypeOverride(acceptedMediaTypes);

    // The initial Format FILTER — kept DISTINCT from the accepted set: it must be
    // a SINGLE value the dropdown can display. Seeding mediaSearch with
    // "image|video" desynced the dropdown (no matching <option> → it fell back to
    // "Images" while the grid listed both images and videos). Default a scene's
    // filter to its current media's type so dropdown and grid agree; the user can
    // switch it, and validation still accepts the whole accepted set.
    const currentSceneMedia = mediasList.find(
      (m) => Number(m.id) === Number(enhancedField.fields.currentValue),
    );
    const initialFilterMediaType = isModel3dField
      ? "model3d"
      : contentType === "content-scene"
      ? currentSceneMedia?.type ?? "image"
      : acceptedMediaTypes;

    dispatch(setMediaSearch({ mediaType: initialFilterMediaType }));
    setCurrentMediaField(enhancedField.fields.fieldName);
    dispatch(setOriginalMediaGallery(enhancedField.fields.currentValue));

    if (menuItem?.itemId === "deleteEnv3d") {
      onMediaChange(null, "source_model3d");
    } else {
      dispatch(openStandardModal("modal-gallery"));
    }
  };

  const onRemoveButtonClick = () => {
    dispatch(openConfirmationModal("remove-node-confirmation"));
  };

  let deleteButton = (
    <button
      className="btn-alternative-outline mr-6 pl-2 pr-4 flex items-center gap-1"
      disabled={!canDelete.canDelete || areButtonsDisabled || !canSave.canSave}
      onClick={onRemoveButtonClick}
    >
      <MdDeleteOutline
        className={`${canDelete.canDelete ? "" : "opacity-50 cursor-not-allowed"}`}
        size={30}
      />
      <span>{t("general.delete")}</span>
    </button>
  );

  if (!canDelete.canDelete) {
    deleteButton = (
      <Tooltip classNameChildren="right-1" message={t(canDelete.reason)}>
        {deleteButton}
      </Tooltip>
    );
  }

  // Rules of Hooks: the two hooks below were previously declared *inside* the
  // `if (node)` branch. `node` comes from the getNodeById selector (a `.find`
  // that returns undefined when the content isn't in the current graph state).
  // It can flip to undefined while this component stays mounted — e.g. when a
  // ScenarIA generation finishes and the graph is rebuilt, or the selected
  // node changes during navigation. On that undefined render the branch was
  // skipped, so fewer hooks ran than the previous render → React #300 → the
  // app ErrorBoundary bounced the user to /error ("scénarIA crashe"). Hoisted
  // here so the hook count stays stable regardless of `node`.
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
    hasSomethingChanged(),
  );

  useEffect(() => {
    if (!node || node.modelName !== "content-scene") return;
    // if content-scene and media was video and it now is image, then display specific warning about pld
    // cf WIXAR-3819  https://wixar.atlassian.net/browse/WIXAR-3819?atlOrigin=eyJpIjoiMjNhY2M2ODhmZTNhNDEzZTk2ZDdhZWU2MjFkMzI0MmIiLCJwIjoiaiJ9
    const changes = getValues();
    const newlySelectedMedia = mediasList.find(
      (media) => Number(media.id) === Number(changes.source_media),
    );
    const isCurrentSceneTypeVideo = node.object.scene_type === "video360";
    const isCurrentSceneTypeImage = node.object.scene_type === "image360";
    const isNewSelectedMediaTypeDifferentFromPreviousOne =
      newlySelectedMedia &&
      ((isCurrentSceneTypeVideo && newlySelectedMedia.type === "image") ||
        (isCurrentSceneTypeImage && newlySelectedMedia.type === "video"));

    if (isNewSelectedMediaTypeDifferentFromPreviousOne) {
      // only display warning when relevant
      if ((projectLanguageDatasOfThisContent?.length ?? 0) > 1) {
        dispatch(
          setAlert({
            type: "warning",
            msg: t("pages.project.editNode.warningSceneMediaTypeChangePldsAreBeingOverriden"),
            subMsg: t(
              "pages.project.editNode.warningSceneMediaTypeChangePldsAreBeingOverridenSubMsg",
            ),
            customTimeoutInMs: 30000,
          }),
        );
      }
    }
  }, [JSON.stringify(enhancedFieldsToDisplay)]);

  if (node) {
    const typeInteractionNameToDispay = node.modelName
      .replaceAll("-", " ")
      .replace("content", "")
      .replace("onboarding", "image 2D")
      .replace("thumbnail", "menu")
      .replace("speech to check", "speech AI");
    const DisplayFormNoMedia =
      enhancedFieldsToDisplay.length === 0 &&
      !enhancedFieldsToDisplay.find((elt) => elt.fields.fieldName === "source_model3d");

    const fieldContentScene = () => {
      const isSourceModel3d = !enhancedFieldsToDisplay.find(
        (elt) => elt.fields.fieldName === "source_model3d",
      )?.fields.currentValue;

      if (node.modelName === "content-scene") {
        if (mode === "edit") {
          if (isSourceModel3d) {
            return (
              <div>
                <h2 className="text-xl w-full max-w-7xl mx-auto mt-6">{t("general.3dEnv")}</h2>
                <div>
                  <button
                    className="btn-alternative-outline mt-2.5 button-enviroment3d text-green-500"
                    onClick={() =>
                      handleOpenGallery(
                        enhancedFieldsToDisplay.find(
                          (elt) => elt.fields.fieldName === "source_model3d",
                        ),
                        null,
                      )
                    }
                    disabled={areButtonsDisabled || !canSave.canSave}
                  >
                    <PlusIcon className="h-6 w-6 ml-36 mb-5" />
                    {t("general.add3dEnv")}
                  </button>
                </div>
              </div>
            );
          } else {
            return <></>;
          }
        } else {
          return (
            <div>
              <h2 className="text-xl w-full max-w-7xl mx-auto mt-6 mb-4">{t("general.3dEnv")}</h2>
              <p className="text-center text-gray-500">{t("general.no3dEnv")}</p>
            </div>
          );
        }
      } else {
        return <></>;
      }
    };

    // A quizz-3 with no correct answer is unplayable in VR — never let it save.
    // Correctness is only editable in the source language, so only gate there;
    // target-language edits (translations only) stay saveable.
    const quizz3CorrectAnswers = watch("correct_answers");
    const quizz3MissingCorrectAnswer =
      node.modelName === "content-quizz-3" &&
      isEditingSourceLanguage &&
      !(Array.isArray(quizz3CorrectAnswers) && quizz3CorrectAnswers.some(Boolean));

    // content-quizz-image-multiple: a quizz with no correct answer is
    // unplayable, and an answer with no real image would render broken in VR.
    // Both are editable only in the source language, so only gate there.
    const imageMultipleAnswers = watch("list_answers");
    const imageMultipleAnswersArr = Array.isArray(imageMultipleAnswers)
      ? (imageMultipleAnswers as any[])
      : [];
    const imageMultipleMissingCorrect =
      node.modelName === "content-quizz-image-multiple" &&
      isEditingSourceLanguage &&
      !imageMultipleAnswersArr.some((a) => a?.is_right);
    const imageMultipleMissingImage =
      node.modelName === "content-quizz-image-multiple" &&
      isEditingSourceLanguage &&
      imageMultipleAnswersArr.some((a) => !(Number(a?.imageId) > 0));
    const quizzImageMultipleInvalid = imageMultipleMissingCorrect || imageMultipleMissingImage;

    // content-quizz-sort: a blank item renders as an empty draggable in VR —
    // unplayable. Text is editable in every language, so block in any language
    // when any answer's text is empty.
    const sortAnswers = watch("answers");
    const sortAnswersArr = Array.isArray(sortAnswers) ? (sortAnswers as any[]) : [];
    const quizzSortMissingText =
      node.modelName === "content-quizz-sort" &&
      sortAnswersArr.some((a) => !((a?.text ?? "") as string).trim());

    // content-random-quizz: each question is an independent mini-quizz.
    //  • per-question no-correct-answer + missing-image (when imageAnswers) are
    //    structural ⇒ source-language only.
    //  • empty question/answer text renders blank in VR ⇒ language-agnostic.
    const randomQuestions = watch("questions");
    const randomQuestionsArr = Array.isArray(randomQuestions)
      ? (randomQuestions as any[])
      : [];
    const isRandomQuizz = node.modelName === "content-random-quizz";
    const randomMissingCorrect =
      isRandomQuizz &&
      isEditingSourceLanguage &&
      randomQuestionsArr.some(
        (q) => !(Array.isArray(q?.answers) && q.answers.some((a: any) => a?.is_right)),
      );
    const randomMissingImage =
      isRandomQuizz &&
      isEditingSourceLanguage &&
      randomQuestionsArr.some(
        (q) =>
          q?.imageAnswers &&
          Array.isArray(q?.answers) &&
          q.answers.some((a: any) => !(Number(a?.imageId) > 0)),
      );
    const randomMissingText =
      isRandomQuizz &&
      randomQuestionsArr.some(
        (q) =>
          !((q?.text ?? "") as string).trim() ||
          !Array.isArray(q?.answers) ||
          q.answers.some((a: any) => !((a?.text ?? "") as string).trim()),
      );
    const quizzRandomInvalid =
      randomMissingCorrect || randomMissingImage || randomMissingText;

    const nextDisabledTooltipText = () => {
      if (isComputingPgSize) {
        return t("tooltip.computingPgSize");
      }
      if (quizz3MissingCorrectAnswer || imageMultipleMissingCorrect || randomMissingCorrect) {
        return t("pages.project.editNode.quizzNeedsCorrectAnswer");
      }
      if (imageMultipleMissingImage || randomMissingImage) {
        return t("pages.project.editNode.quizzImageNeedsImage");
      }
      if (quizzSortMissingText) {
        return t("pages.project.editNode.quizzSortNeedsText");
      }
      if (randomMissingText) {
        return t("pages.project.editNode.quizzRandomNeedsText");
      }
      if (node.modelName === "content-scene") {
        if (watch("name") && watch("name").length === 0) {
          return t("tooltip.pleaseNameTheScene");
        }
        // search the media.all & if it doesnt include nodeToEdit.object.source_media display specific message
        const mediaExists = mediasList.find(
          (media) => Number(media.id) === node.object?.source_media,
        );
        if (!mediaExists) {
          return t("tooltip.mediaNeeded");
        }
      }

      if (!isComputingPgSize && isOverProjectGroupMediaWeightLimit) {
        return t("pages.project.editNode.overProjectGroupMediaWeightLimit");
      } else {
        return t(canSave.reason);
      }
    };

    // Single execution path for the contextual translate button. Called
    // directly when no overwrite is at risk, or from the overwrite-confirm
    // modal's onClickSubmit when the user agrees to clobber existing
    // translations.
    const runTranslation = (targetIds: number[]) => {
      setIsTranslating(true);
      const action = dispatch(
        translateProject({
          projectId: currentProject.id,
          targetLanguageIds: targetIds,
          contentId,
          cb: () => {
            dispatch(refetchPldsForContent(contentId));
            dispatch(
              setAlert({
                type: "success",
                msg: t("pages.project.editNode.translateActionSuccess"),
              }),
            );
          },
          errorCb: () => {
            dispatch(
              setAlert({
                type: "error",
                msg: t("pages.project.editNode.translateActionError"),
              }),
            );
          },
        }),
      );
      Promise.resolve(action).finally(() => setIsTranslating(false));
    };

    return (
      <>
        <ModalGallery
          nodeId={nodeId}
          contentType={contentType}
          onMediaChange={onMediaChange}
          allowedMediaTypeOverride={allowedMediaTypeOverride}
        />
        <ModalConfirmation
          title={t(
            `pages.project.editNode.${
              node.modelName === "content-scene"
                ? "deleteSceneConfirmationModal"
                : "deleteInteractionConfirmationModal"
            }.header`,
          )}
          text={t(
            `pages.project.editNode.${
              node.modelName === "content-scene"
                ? "deleteSceneConfirmationModal"
                : "deleteInteractionConfirmationModal"
            }.body`,
          )}
          onClickSubmit={onRemove}
          id={"remove-node-confirmation"}
        />
        <ModalConfirmation
          id="translate-overwrite-confirmation"
          title={t("pages.project.editNode.translateOverwriteConfirmTitle") as string}
          text={
            t(
              isEditingSourceLanguage
                ? "pages.project.editNode.translateAllOverwriteConfirm"
                : "pages.project.editNode.translateInteractionOverwriteConfirm",
              { language: prettyLanguageName(activeLanguageId) },
            ) as string
          }
          submitButtonText={t("pages.project.editNode.translateOverwriteConfirmSubmit") as string}
          onClickSubmit={() => {
            const ids = pendingTranslateTargetIds;
            setPendingTranslateTargetIds(null);
            dispatch(closeConfirmationModal("translate-overwrite-confirmation"));
            if (ids && ids.length > 0) runTranslation(ids);
          }}
          onClickCancel={() => setPendingTranslateTargetIds(null)}
        />

        <div className="flex flex-col w-full flex-1 min-h-0 mt-4 mx-auto">
          <IsLoading componentId={NODE_COMPONENT_ID} spinnerPlaceholder>
            <form
              className="relative w-full flex-1 min-h-0 overflow-y-auto personalize-scroll-visible"
              onSubmit={handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                // Disable Enter-to-submit on form text inputs and re-route
                // Enter to the rail's send button. The composer handles its
                // own Enter via preventDefault + internal handleSubmit; we
                // detect that here via e.defaultPrevented and bow out.
                if (e.key !== "Enter" || e.shiftKey) return;
                if (e.defaultPrevented) return;
                const target = e.target as HTMLElement;
                if (target.tagName !== "INPUT") return;
                e.preventDefault();
                const sendBtn =
                  e.currentTarget.querySelector<HTMLButtonElement>("[data-rail-send]");
                if (sendBtn && !sendBtn.disabled) sendBtn.click();
              }}
            >
              <ScopeProvider
                modelName={String(node.modelName)}
                active={mode === "edit"}
                listAnswersCount={listAnswersCount}
                answersCount={answersCount}
                questionsCount={questionsCount}
              >
                <div
                  className={
                    isSlopifySupported && mode === "edit"
                      ? "mx-auto max-w-7xl grid grid-cols-[1fr_480px] gap-6"
                      : "max-w-3xl mx-auto"
                  }
                >
                  <div className="min-w-0 relative">
                    <div className="w-full flex flex-col">
                      <div className="mb-4 flex items-baseline justify-center gap-3">
                        <h2 className="text-xl font-bold">
                          {t(mode === "edit" ? "general.edit" : "general.view")}
                          <span className="capitalize">{`: ${typeInteractionNameToDispay.trim()}`}</span>
                        </h2>
                        <span className="text-sm italic text-gray-600">{`${t(
                          "pages.editProjectLanguage.projectMainLanguage",
                        )}: ${prettyLanguageName(sourceLanguageId)}`}</span>
                      </div>
                      {projectLanguageDatasOfThisContent &&
                        projectLanguageDatasOfThisContent.length > 1 && (
                          <div className="mb-4 flex items-center justify-center gap-3 flex-wrap">
                            <LanguageSwitcher
                              plds={projectLanguageDatasOfThisContent}
                              activeLanguageId={activeLanguageId}
                              sourceLanguageId={sourceLanguageId}
                              staleCountByLanguageId={Object.fromEntries(
                                Object.entries(staleFieldsByLanguageId).map(([k, v]) => [
                                  Number(k),
                                  v.size,
                                ]),
                              )}
                              onChange={(id) => {
                                if (id === activeLanguageId) return;
                                if (hasSomethingChanged()) {
                                  // eslint-disable-next-line no-alert
                                  const confirmed = window.confirm(
                                    t("pages.project.editNode.switchLanguageDiscardChanges"),
                                  );
                                  if (!confirmed) return;
                                }
                                reset({}, { keepValues: false });
                                setActiveLanguageId(id);
                              }}
                            />
                            {(() => {
                              // Contextual DeepL translate button. Two shapes:
                              //  • on source tab → "Traduire dans toutes les
                              //    langues" (fan out this interaction to every
                              //    other enabled language).
                              //  • on a target tab → "Traduire cette
                              //    interaction" (pull this interaction from
                              //    source into the active language).
                              // Both call the same backend (translateProject
                              // with `contentId`) — only `targetLanguageIds`
                              // changes.
                              const otherLangPlds =
                                projectLanguageDatasOfThisContent?.filter(
                                  (p: any) => Number(p.language) !== Number(sourceLanguageId),
                                ) ?? [];
                              const otherEnabledLanguageIds = otherLangPlds.map((p: any) =>
                                Number(p.language),
                              );

                              const sourceMode = isEditingSourceLanguage;
                              const visible = sourceMode
                                ? otherEnabledLanguageIds.length > 0
                                : true;
                              if (!visible) return null;

                              const label = sourceMode
                                ? t("pages.project.editNode.translateAllLanguagesAction")
                                : t("pages.project.editNode.translateInteractionAction");

                              const targetIds = sourceMode
                                ? otherEnabledLanguageIds
                                : [activeLanguageId];

                              const onClick = () => {
                                // Pre-flight: drop in-form unsaved edits if
                                // the user agrees (matches the language-
                                // switcher pattern; existing window.confirm).
                                if (hasSomethingChanged()) {
                                  // eslint-disable-next-line no-alert
                                  const confirmed = window.confirm(
                                    t("pages.project.editNode.switchLanguageDiscardChanges"),
                                  );
                                  if (!confirmed) return;
                                  reset({}, { keepValues: false });
                                }
                                // Overwrite confirmation: if any target PLD
                                // already has translatable content, route
                                // through the redux-driven ModalConfirmation
                                // (rendered below near the delete-confirm
                                // modal) instead of a native window.confirm.
                                const willOverwrite = sourceMode
                                  ? otherLangPlds.some((p: any) => pldHasTranslatableContent(p))
                                  : pldHasTranslatableContent(activePld);
                                if (willOverwrite) {
                                  setPendingTranslateTargetIds(targetIds);
                                  dispatch(
                                    openConfirmationModal("translate-overwrite-confirmation"),
                                  );
                                  return;
                                }
                                runTranslation(targetIds);
                              };

                              return (
                                <button
                                  type="button"
                                  className="btn-primary-outline text-xs inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
                                  disabled={isTranslating || areButtonsDisabled || !canSave.canSave}
                                  onClick={onClick}
                                >
                                  {isTranslating && (
                                    <svg
                                      className="animate-spin h-3.5 w-3.5"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      />
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      />
                                    </svg>
                                  )}
                                  {label}
                                </button>
                              );
                            })()}
                          </div>
                        )}
                      {!isEditingSourceLanguage && (activeStaleFields?.size ?? 0) > 0 && (
                        <div className="mb-4 p-3 rounded-lg bg-yellow-100 text-yellow-700 flex items-start gap-3">
                          <InformationCircleIcon className="w-5 h-5 mt-0.5 shrink-0" />
                          <div className="flex-1 text-sm font-medium">
                            {t("pages.project.editNode.staleBannerTitle", {
                              count: activeStaleFields?.size ?? 0,
                              language: prettyLanguageName(activeLanguageId),
                            })}
                          </div>
                        </div>
                      )}
                      <div className={DisplayFormNoMedia ? "mx-auto w-full max-w-2xl" : "w-full"}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                          {fieldsToDisplay &&
                            fieldsToDisplay
                              .filter((field: Field) => field.type !== "media")
                              .map((field: Field, index: number) => {
                                // content-quizz-3: bespoke paired editor for
                                // list_answers + correct_answers + has_multiple_answers.
                                // correct_answers is folded into the editor, so
                                // skip its standalone (placeholder) render.
                                if (node.modelName === "content-quizz-3") {
                                  if (field.fieldName === "correct_answers") return null;
                                  if (field.fieldName === "list_answers") {
                                    const flashClass = justApplied.has(field.fieldName)
                                      ? "slopify-flash"
                                      : "";
                                    return (
                                      <div
                                        key={`${field.fieldName}-${activeLanguageId}-${index}`}
                                        className={`md:col-span-2 ${flashClass}`.trim()}
                                      >
                                        <QuizThreeAnswersField
                                          control={control}
                                          setValue={setValue}
                                          label={prettyLabel("list_answers", t as any)}
                                          disabled={mode !== "edit" || isGenerating}
                                          isSourceLanguage={isEditingSourceLanguage}
                                          maxLength={
                                            field.descriptor?.jsonSchema?.items?.maxLength as
                                              | number
                                              | undefined
                                          }
                                        />
                                      </div>
                                    );
                                  }
                                }
                                // content-quizz-image-multiple: bespoke editor
                                // over the list_answers [{ text, imageId,
                                // is_right }] object array, with a per-row image
                                // picker reusing the shared gallery modal.
                                if (
                                  node.modelName === "content-quizz-image-multiple" &&
                                  field.fieldName === "list_answers"
                                ) {
                                  const flashClass = justApplied.has(field.fieldName)
                                    ? "slopify-flash"
                                    : "";
                                  return (
                                    <div
                                      key={`${field.fieldName}-${activeLanguageId}-${index}`}
                                      className={`md:col-span-2 ${flashClass}`.trim()}
                                    >
                                      <QuizImageMultipleAnswersField
                                        control={control}
                                        setValue={setValue}
                                        label={prettyLabel("list_answers", t as any)}
                                        mediasList={mediasList}
                                        onPickImage={onPickAnswerImage}
                                        disabled={mode !== "edit" || isGenerating}
                                        isSourceLanguage={isEditingSourceLanguage}
                                        maxLength={
                                          field.descriptor?.jsonSchema?.items?.properties?.text
                                            ?.maxLength as number | undefined
                                        }
                                      />
                                    </div>
                                  );
                                }
                                // content-quizz-sort: bespoke drag-to-reorder
                                // editor over the answers [{ text, index,
                                // id_internal }] object array. Display order =
                                // correct order; dragging recomputes `index`.
                                if (
                                  node.modelName === "content-quizz-sort" &&
                                  field.fieldName === "answers"
                                ) {
                                  const flashClass = justApplied.has(field.fieldName)
                                    ? "slopify-flash"
                                    : "";
                                  return (
                                    <div
                                      key={`${field.fieldName}-${activeLanguageId}-${index}`}
                                      className={`md:col-span-2 ${flashClass}`.trim()}
                                    >
                                      <QuizSortAnswersField
                                        control={control}
                                        setValue={setValue}
                                        label={prettyLabel("answers", t as any)}
                                        disabled={mode !== "edit" || isGenerating}
                                        isSourceLanguage={isEditingSourceLanguage}
                                        maxLength={
                                          field.descriptor?.jsonSchema?.items?.properties?.text
                                            ?.maxLength as number | undefined
                                        }
                                      />
                                    </div>
                                  );
                                }
                                // content-random-quizz: bespoke doubly-nested
                                // accordion editor over the questions [{ text,
                                // answers:[{ text, imageId, is_right }], winText,
                                // loseText, imageAnswers, allowMultiple }] array.
                                if (
                                  node.modelName === "content-random-quizz" &&
                                  field.fieldName === "questions"
                                ) {
                                  const flashClass = justApplied.has(field.fieldName)
                                    ? "slopify-flash"
                                    : "";
                                  return (
                                    <div
                                      key={`${field.fieldName}-${activeLanguageId}-${index}`}
                                      className={`md:col-span-2 ${flashClass}`.trim()}
                                    >
                                      <QuizRandomQuizzField
                                        control={control}
                                        setValue={setValue}
                                        label={prettyLabel("questions", t as any)}
                                        mediasList={mediasList}
                                        onPickImage={onPickRandomAnswerImage}
                                        disabled={mode !== "edit" || isGenerating}
                                        isSourceLanguage={isEditingSourceLanguage}
                                        maxLength={
                                          field.descriptor?.jsonSchema?.items?.properties?.answers
                                            ?.items?.properties?.text?.maxLength as
                                            | number
                                            | undefined
                                        }
                                      />
                                    </div>
                                  );
                                }
                                const locallyCleared = !!dirtyFields[field.fieldName];
                                const showStale =
                                  !locallyCleared && !!activeStaleFields?.has(field.fieldName);
                                const flashClass = justApplied.has(field.fieldName)
                                  ? "slopify-flash"
                                  : "";
                                return (
                                  <div
                                    key={`${field.fieldName}-${activeLanguageId}-${index}`}
                                    className={`${
                                      isWideField(field) ? "md:col-span-2 " : ""
                                    }${flashClass}`.trim()}
                                  >
                                    <DynamicField
                                      field={field}
                                      register={register}
                                      control={control}
                                      disabled={mode !== "edit" || isGenerating}
                                      className="dynamic-field"
                                      staleWarning={showStale}
                                    />
                                  </div>
                                );
                              })}
                          {process.env.REACT_APP_ENABLE_RAG_AGENTS_PEOPLE_AND_COACH_AI === "true" &&
                            (node.modelName === "content-avatar-ai-persona" ||
                              node.modelName === "content-avatar-ai") && (
                              <div
                                className={
                                  node.modelName === "content-avatar-ai-persona" &&
                                  (node.object as Record<string, unknown>).activate_debriefing
                                    ? "md:col-span-2 grid grid-cols-2 gap-4"
                                    : ""
                                }
                              >
                                <Controller
                                  control={control}
                                  name="rag_agent_ids"
                                  render={({ field }) => (
                                    <RagAgentMultiSelect
                                      title={
                                        node.modelName === "content-avatar-ai-persona"
                                          ? t("pages.agents.ragAgentSelect.titleConversation")
                                          : undefined
                                      }
                                      description={
                                        node.modelName === "content-avatar-ai-persona"
                                          ? t("pages.agents.ragAgentSelect.descriptionConversation")
                                          : undefined
                                      }
                                      value={field.value || []}
                                      onChange={(ids) => field.onChange(ids)}
                                      disabled={mode !== "edit" || isGenerating}
                                    />
                                  )}
                                />
                                {node.modelName === "content-avatar-ai-persona" &&
                                  (node.object as Record<string, unknown>).activate_debriefing && (
                                    <Controller
                                      control={control}
                                      name="rag_agent_ids_debrief"
                                      render={({ field }) => (
                                        <RagAgentMultiSelect
                                          title={t("pages.agents.ragAgentSelect.titleDebrief")}
                                          description={t(
                                            "pages.agents.ragAgentSelect.descriptionDebrief",
                                          )}
                                          value={field.value || []}
                                          onChange={(ids) => field.onChange(ids)}
                                          disabled={mode !== "edit" || isGenerating}
                                        />
                                      )}
                                    />
                                  )}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>

                    <div
                      className={`${DisplayFormNoMedia ? "ml-11" : ""} flex flex-wrap pt-2 w-full`}
                    >
                      {spinnerComponent}

                      {enhancedFieldsToDisplay.length > 0 &&
                        enhancedFieldsToDisplay.map((enhancedField, index: number) => {
                          let isEnv3d = false;
                          if (
                            enhancedField.fields.fieldName === "source_model3d" &&
                            node.modelName === "content-scene"
                          ) {
                            if (!enhancedField.fields.currentValue) {
                              return <div key={index}></div>;
                            } else {
                              isEnv3d = true;
                            }
                          }

                          return (
                            <div
                              key={`${mode}-node-${index}`}
                              className={`mt-2 w-80 shrink-0 mr-9 ${isEnv3d ? "w-80 h-72" : ""}`}
                            >
                              {isEnv3d ? (
                                <h2 className="text-xl w-full max-w-7xl mx-auto mb-4 mt-4">
                                  {t("general.3dEnv")}
                                </h2>
                              ) : (
                                // Reserve a consistent heading height and bottom-align the
                                // text so adjacent media cards (e.g. quizz-3's win/loose
                                // sounds, whose labels wrap to different line counts) start
                                // at the same vertical offset instead of staggering.
                                <div className="mb-4 mt-4 min-h-[3.5rem] flex items-end">
                                  <h2 className="text-xl w-full max-w-7xl mx-auto">
                                    {prettyLabel(enhancedField.fields.fieldName, t as any)}
                                    {isOptionalMediaField(
                                      String(node.modelName),
                                      enhancedField.fields.fieldName,
                                    ) && (
                                      <span className="ml-2 text-sm font-normal italic text-gray-500">
                                        {t("general.optional")}
                                      </span>
                                    )}
                                  </h2>
                                </div>
                              )}

                              <Controller
                                control={control}
                                name={enhancedField.fields.fieldName}
                                render={({ field }) => (
                                  <DynamicMedia
                                    className="dynamic-media"
                                    field={field}
                                    fieldType={enhancedField.fields.type}
                                    register={register}
                                    stopPlayingMedia={stopPlayingMedia}
                                    handleButtonClick={(_mediaId, item) => {
                                      setStopPlayingMedia(!stopPlayingMedia);
                                      if (mode === "edit") {
                                        handleOpenGallery(enhancedField, item);
                                      } else if (
                                        enhancedField.relevantSourceMedia?.type !== "pdf"
                                      ) {
                                        dispatch(
                                          openMediaPreviewModal(
                                            `preview_${enhancedField.relevantSourceMedia?.id}`,
                                          ),
                                        );
                                      }
                                    }}
                                    currentMedia={enhancedField.fields.currentValue as number}
                                    media={enhancedField.relevantSourceMedia as IMedia}
                                    customMenuItems={
                                      mode === "edit" && isEnv3d ? model3dMenuItems : []
                                    }
                                    disabled={mode !== "edit" || !isEditingSourceLanguage}
                                    showThreeDotsMenu={mode === "edit" && isEditingSourceLanguage}
                                    CustomRemoveButton={
                                      mode === "edit" && isEnv3d
                                        ? {
                                            component: CustomXIcon,
                                            customProps: {
                                              className:
                                                "stroke-black w-6 h-6 absolute right-3 top-4 hover:stroke-gray-600",
                                              onClick: (e: Event) => {
                                                e.stopPropagation();
                                                onMediaChange(null, "source_model3d");
                                              },
                                              tooltipText: t("general.remove3dEnv"),
                                            },
                                          }
                                        : undefined
                                    }
                                  />
                                )}
                              />
                            </div>
                          );
                        })}
                      {fieldContentScene()}
                    </div>
                    {node.modelName === "content-scene" && mode === "edit" && (
                      <StartSceneTickbox
                        disabled={!canSave.canSave}
                        wasStartScene={node.object.is_start_scene}
                        isStartScene={isStartScene}
                        setIsStartScene={() => {
                          setIsStartScene(!isStartScene);
                        }}
                      />
                    )}
                  </div>
                  {mode === "edit" && isSlopifySupported && (
                    <aside className="min-w-0 sticky top-0 self-start h-[calc(100vh-19rem)]">
                      <AssistantScenarIaRail
                        contentId={contentId}
                        activeLanguageId={activeLanguageId}
                        sourceLanguageId={sourceLanguageId}
                        contentTypeModelName={String(node.modelName)}
                        disabled={areButtonsDisabled || !canSave.canSave}
                        setValue={setValue}
                        getValues={getValues}
                        setHasGeneratedContent={setHasGeneratedContent}
                        onGeneratingChange={setIsGenerating}
                        onFieldsApplied={handleFieldsApplied}
                        dirtyFieldNames={dirtyFieldNames}
                        otherEnabledLanguageIds={otherEnabledLanguageIds}
                        staleFieldsByLanguageId={staleFieldsByLanguageId}
                        allFillableFieldNames={translatableFieldNames}
                        onApplyLanguageTranslations={handleApplyLanguageTranslations}
                      />
                    </aside>
                  )}
                  {/*
                    NOTE: a <DisabledRail /> used to render here when
                    !isSlopifySupported, showing "Bientôt disponible pour ce
                    type de contenu". This is bad UX — users opening avatar-AI,
                    scene, photo-catch, scoring-scene, clean, dust,
                    audio-player, etc. now get zero signal that the Assistant
                    Scenar IA feature exists or is planned for their content
                    type, which is opaque and discoverability-hostile.
                    But this is what was asked for, so it was done.
                  */}
                </div>
              </ScopeProvider>
            </form>
          </IsLoading>
          {mode === "edit" && (
            <FooterButtons
              cancelCb={() => {
                if (location.pathname.includes("templates")) {
                  navigate(`/templates/${currentProject.id}`);
                } else {
                  navigate("../graph");
                }
              }}
              cancelText={t("general.cancel")}
              nextCb={onUpdateNode}
              nextText={t("general.save")}
              nextDisabledTooltipText={nextDisabledTooltipText()}
              nextDisabled={
                areButtonsDisabled ||
                !canSave.canSave ||
                !hasSomethingChanged() ||
                !nameCantBeEmpty ||
                isComputingPgSize ||
                isOverProjectGroupMediaWeightLimit ||
                quizz3MissingCorrectAnswer ||
                quizzImageMultipleInvalid ||
                quizzSortMissingText ||
                quizzRandomInvalid
              }
              customButton={deleteButton}
              customButtonIndex={1}
            />
          )}
        </div>
        {mode === "edit" && (
          <ModalConfirmBeforeNavigatingOut
            showDialog={showPrompt}
            cancelNavigation={cancelNavigation}
            confirmNavigation={confirmNavigation}
          />
        )}
      </>
    );
  } else {
    return <>Cant find the node to {mode}</>;
  }
};

export default Node;
